import React from "react";
import {observer} from "mobx-react-lite";
import {Alert} from "@mui/material";
import type {GridColDef, GridSortModel} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import {Tag} from "~/components/tag/Tag.tsx";
import {useStores} from "~/stores";
import type {ContractDetail, ContractLink, LinkStatus} from "../contractExplorerTypes.ts";
import {formatDate, formatMoney, formatNumber, lineName, MISSING, spendColumnField} from "../contractExplorerFormat.ts";
import {useContractLinksQuery} from "../useContractLinksQuery.ts";
import {CompetingBundles} from "./CompetingBundles.tsx";
import {ConfidenceBadge} from "./ConfidenceBadge.tsx";

type Props = {
    bagId: number;
    contract: ContractDetail;
    status: LinkStatus;
    onOpenContract: (contractId: number) => void;
};

const STATUS = {
    linked: {label: 'Linked', color: 'success'},
    competing: {label: 'Competing', color: 'warning'},
    candidate: {label: 'Candidate', color: 'default'},
} as const;

/** Grid column → the API's ordering key. */
const ORDERING: Record<string, string> = {
    p_id: 'part__p_id', date: 'part__p_ddate_1', spend: 'part__p_spend', quantity: 'part__p_quantity',
    unit_price: 'part__p_unit_spend', confidence: 'confidence', line: 'line',
};
const PAGE_SIZE = 10;
const DESCRIPTION = 'part__p_description';

/**
 * One status of the bundle's linked transactions, a page at a time, with a
 * column per description column linkage read, labelled as the databag names it.
 */
export const LinksGrid: React.FC<Props> = observer(({bagId, contract, status, onOpenContract}) => {
    const {bagStore} = useStores();
    const {configStore} = bagStore;
    const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: PAGE_SIZE});
    const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
    const [sort] = sortModel;
    const ordering = sort && ORDERING[sort.field] ? `${sort.sort === 'desc' ? '-' : ''}${ORDERING[sort.field]}` : undefined;
    const {data, isFetching, error} = useContractLinksQuery(bagId, contract.id, {status, ordering, ...paginationModel});
    const currency = contract.spend_currency;
    const spendColumns = contract.spend_columns;
    // A description column of its own takes the description's place in the Transaction cell.
    const ownDescription = spendColumns.includes(DESCRIPTION);
    const databagConfig = configStore.databagConfig;

    const columns = React.useMemo<GridColDef<ContractLink>[]>(() => [
        {
            field: 'p_id', headerName: 'Transaction', minWidth: 200, flex: 1.4,
            renderCell: ({row}) => (
                ownDescription
                    ? <span className="ci-cname">
                        <b className="ci-id">#{row.p_id}</b>
                        {row.supplier && <span className="ci-csub">{row.supplier}</span>}
                    </span>
                    : <span className="ci-cname">
                        <b title={row.description}>{row.description || row.p_id}</b>
                        <span className="ci-csub"><span className="ci-id">#{row.p_id}</span>{row.supplier && `· ${row.supplier}`}</span>
                    </span>
            ),
        },
        ...spendColumns.map((name): GridColDef<ContractLink> => ({
            minWidth: 160, flex: 1.2,
            ...configStore.getPartialGridColDef(spendColumnField(name), spendColumnField(name)),
            field: `column:${name}`, sortable: false, type: 'string',
            valueGetter: (_value, row) => row.columns[name] ?? '',
            renderCell: ({value}) => <span className="ci-reason" title={value}>{value}</span>,
        })),
        {field: 'date', headerName: 'Date', width: 120, renderCell: ({row}) => formatDate(row.date)},
        {field: 'entity', headerName: 'Buying entity', width: 170, sortable: false},
        {field: 'spend', headerName: currency ? `Spend (${currency})` : 'Spend', width: 120, align: 'right',
            headerAlign: 'right', renderCell: ({row}) => <span className="ci-mono">{formatMoney(row.spend)}</span>},
        {field: 'quantity', headerName: 'Quantity', width: 110, align: 'right', headerAlign: 'right',
            renderCell: ({row}) => (
                <span className="ci-mono">
                    {row.quantity === null ? MISSING : `${formatNumber(row.quantity)}${row.unit ? ` ${row.unit}` : ''}`}
                </span>
            )},
        {field: 'unit_price', headerName: currency ? `Unit price (${currency})` : 'Unit price', width: 130,
            align: 'right', headerAlign: 'right',
            renderCell: ({row}) => <span className="ci-mono">{row.unit_price === null ? MISSING : formatNumber(row.unit_price)}</span>},
        {field: 'status', headerName: 'Match state', width: 120, sortable: false,
            renderCell: ({row}) => <Tag label={STATUS[row.status].label} color={STATUS[row.status].color}/>},
        {field: 'confidence', headerName: 'Confidence', width: 110, align: 'right', headerAlign: 'right',
            renderCell: ({row}) => <ConfidenceBadge value={row.confidence}/>},
        {field: 'line', headerName: 'Contract line', minWidth: 190, flex: 1,
            renderCell: ({row}) => (
                <span className="ci-cname">
                    <span title={lineName(contract.versions, row.line)}>{lineName(contract.versions, row.line)}</span>
                    <span className="ci-csub">{row.line ? `${row.line} · version ${row.version_no}` : `version ${row.version_no}`}</span>
                </span>
            )},
        {field: 'reason', headerName: 'Match reason', minWidth: 240, flex: 1.6, sortable: false,
            renderCell: ({row}) => <span className="ci-reason" title={row.reason}>{row.reason}</span>},
        {field: 'competing_with', headerName: 'Competing', width: 130, sortable: false,
            renderCell: ({row}) => <CompetingBundles rivals={row.competing_with} onOpen={onOpenContract}/>},
    // getPartialGridColDef reads databagConfig, so the headers follow the databag's labels once they load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [contract.versions, currency, onOpenContract, spendColumns, ownDescription, configStore, databagConfig]);

    if (error) return <Alert severity="error">The transactions could not be loaded: {error.message}</Alert>;
    return (
        <div className="ci-grid">
            <CustomDataGrid
                rows={data?.results ?? []}
                columns={columns}
                autoHeight
                rowSize="small"
                autoRowHeight
                loading={isFetching && !data}
                rowCount={data?.count ?? 0}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[PAGE_SIZE, 25, 50]}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={model => {
                    setSortModel(model);
                    setPaginationModel(p => ({...p, page: 0}));
                }}
                disableRowSelectionOnClick
            />
        </div>
    );
});
